import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import {
    Wifi,
    Users,
    BedDouble,
    Bath,
    UtensilsCrossed,
    Sparkles,
    MapPin,
    Instagram,
    Facebook,
    ArrowRight,
    ArrowUpRight,
    Menu,
    X,
    Star,
    Flame,
    TreePine,
    Send,
    CalendarCheck,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { HOME } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO = "/assets/glamping-logo.png";

// Hostaway booking widget config. `BOOKING_BASE_URL` points at the custom
// Hostaway Booking Website domain the owner set up in the dashboard.
// `HOSTAWAY_LISTING_ID` is currently the Large Dome (only live listing).
// Small Dome ID will be added here once its listing goes live on Hostaway.
const BOOKING_BASE_URL = "https://book.glampinginparadise.com/";
const HOSTAWAY_LISTING_ID = 580273; // Large Dome
const HOSTAWAY_BOOKING_URL = "https://book.glampinginparadise.com";

// -----------------------------------------------------------------------------
// Static content
// -----------------------------------------------------------------------------
const STAYS = [
    {
        id: "small-dome",
        testId: HOME.stayCardSmallDome,
        bookTestId: HOME.stayBookSmallDome,
        name: "Small Dome",
        subtitle: "A cozy dome for two",
        image:
            "https://images.unsplash.com/photo-1777768785762-9a6ad40618ae?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
        price: 199.95,
        priceLabel: "/ night",
        capacity: "2 guests",
        bed: "1 double bed",
        bath: "1 bath",
        highlights: ["Kitchenette", "Internet included", "Private deck"],
        status: "available",
    },
    {
        id: "large-dome",
        testId: HOME.stayCardLargeDome,
        bookTestId: HOME.stayBookLargeDome,
        name: "Large Dome",
        subtitle: "Room for the whole crew",
        image:
            "https://images.unsplash.com/photo-1774013710036-982f187dfe38?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
        price: 299.95,
        priceLabel: "/ night",
        capacity: "4 guests",
        bed: "2 full-size beds",
        bath: "1 bath",
        highlights: ["Internet included", "Private deck", "Fire pit access"],
        status: "available",
    },
];

const GALLERY = [
    "https://images.unsplash.com/photo-1510568192-5cd6e7a47edd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    "https://images.unsplash.com/photo-1526491109672-74740652b963?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    "https://images.unsplash.com/photo-1533105079780-92b9be482077?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    "https://images.unsplash.com/photo-1516426122078-c23e76319801?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    "https://images.unsplash.com/photo-1571863533956-01c88e79957e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
];

const HERO_VIDEO =
    "https://videos.pexels.com/video-files/1409899/1409899-hd_1920_1080_25fps.mp4";
const BUS_VIDEO =
    "https://videos.pexels.com/video-files/6981419/6981419-hd_1920_1080_25fps.mp4";
const BUS_STILL =
    "https://images.unsplash.com/photo-1495819427834-1954f20ae2a6?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

// -----------------------------------------------------------------------------
// Small building blocks
// -----------------------------------------------------------------------------
const Sparkle = ({ className = "", size = 14 }) => (
    <Sparkles className={`sparkle ${className}`} size={size} strokeWidth={1.4} />
);

const Eyebrow = ({ children, className = "" }) => (
    <span
        className={`font-mono-alt text-sage ${className}`}
        style={{ letterSpacing: "0.32em" }}
    >
        {children}
    </span>
);

// -----------------------------------------------------------------------------
// Header
// -----------------------------------------------------------------------------
const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const links = [
        { label: "Stays", href: "#stays", testId: HOME.headerNavStays },
        { label: "Book", href: "#book", testId: "header-nav-book" },
        { label: "Gallery", href: "#gallery", testId: HOME.headerNavGallery },
        { label: "About", href: "#about", testId: HOME.headerNavAbout },
        { label: "Contact", href: "#contact", testId: HOME.headerNavContact },
    ];

    return (
        <header
            className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
                scrolled
                    ? "backdrop-blur-md bg-[rgba(253,252,240,0.82)] border-b border-[rgba(196,165,117,0.25)]"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                <a
                    href="#top"
                    data-testid={HOME.headerLogo}
                    className="flex items-center gap-3 group"
                    aria-label="Glamping in Paradise home"
                >
                    <img
                        src={LOGO}
                        alt="Glamping in Paradise"
                        className="h-12 w-12 md:h-14 md:w-14 object-contain transition-transform duration-500 group-hover:rotate-[-4deg]"
                    />
                    <span className="hidden sm:flex flex-col leading-none">
                        <span className="font-display text-forest text-xl md:text-[1.35rem]">
                            Glamping
                        </span>
                        <span className="font-mono-alt text-sage mt-0.5">
                            In Paradise
                        </span>
                    </span>
                </a>

                <nav className="hidden md:flex items-center gap-10">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={l.testId}
                            className="text-forest/85 hover:text-forest text-[0.95rem] tracking-wide transition-colors relative after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-px after:bg-gold after:origin-left after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-500"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href="#book"
                        data-testid={HOME.headerBookCta}
                        className="btn-gold hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium"
                    >
                        Book Now <ArrowRight size={16} />
                    </a>
                    <button
                        type="button"
                        className="md:hidden text-forest p-2"
                        aria-label="Toggle menu"
                        onClick={() => setOpen((v) => !v)}
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="md:hidden bg-cream border-t border-[rgba(196,165,117,0.25)] px-6 py-6 flex flex-col gap-5">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`${l.testId}-mobile`}
                            className="text-forest font-display text-2xl"
                            onClick={() => setOpen(false)}
                        >
                            {l.label}
                        </a>
                    ))}
                    <a
                        href="#book"
                        onClick={() => setOpen(false)}
                        className="btn-gold inline-flex w-fit items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium"
                    >
                        Book Now <ArrowRight size={16} />
                    </a>
                </div>
            )}
        </header>
    );
};

// -----------------------------------------------------------------------------
// Hero
// -----------------------------------------------------------------------------
const Hero = () => {
    return (
        <section
            id="top"
            className="relative min-h-[100svh] w-full overflow-hidden bg-charcoal"
        >
            <video
                data-testid={HOME.heroVideo}
                className="absolute inset-0 h-full w-full object-cover"
                src={HERO_VIDEO}
                poster="https://images.unsplash.com/photo-1483401757487-2ced3fa77952?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
            />
            <div className="video-scrim" />

            {/* Decorative sparkles pulled from the logo */}
            <Sparkle className="top-24 left-[12%] animate-twinkle" size={18} />
            <Sparkle
                className="top-40 right-[18%] animate-twinkle"
                size={12}
                style={{ animationDelay: "1.2s" }}
            />
            <Sparkle
                className="bottom-40 left-[22%] animate-twinkle"
                size={14}
                style={{ animationDelay: "0.6s" }}
            />

            <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-40 pb-24 md:pt-52 md:pb-40 min-h-[100svh] flex flex-col justify-end md:justify-center">
                <div className="max-w-3xl">
                    <div className="flex items-center gap-3 mb-6 animate-fadeUp">
                        <span className="h-px w-10 bg-gold" />
                        <span className="font-mono-alt text-gold">
                            Domes · Stars · Slow Nights
                        </span>
                    </div>

                    <h1
                        data-testid={HOME.heroTitle}
                        className="font-display text-cream text-[3.2rem] leading-[1.02] md:text-[5.6rem] md:leading-[0.98] animate-fadeUp"
                        style={{ animationDelay: "0.15s" }}
                    >
                        Sleep beneath
                        <br />
                        the <span className="italic text-gold">whole</span> sky.
                    </h1>

                    <p
                        className="mt-8 max-w-xl text-cream/85 text-lg md:text-xl leading-relaxed animate-fadeUp"
                        style={{ animationDelay: "0.35s" }}
                    >
                        Geodesic domes tucked into the trees, a Star Wars themed
                        bus on the way, and a night sky that goes on for
                        forever. Welcome to your paradise.
                    </p>

                    <div
                        className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 animate-fadeUp"
                        style={{ animationDelay: "0.5s" }}
                    >
                        <a
                            href="#book"
                            data-testid={HOME.heroBookCta}
                            className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-[0.95rem] font-medium"
                        >
                            Reserve your stay <ArrowRight size={17} />
                        </a>
                        <a
                            href="#stays"
                            data-testid={HOME.heroExploreCta}
                            className="btn-ghost inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-[0.95rem]"
                        >
                            Explore the domes
                        </a>
                    </div>
                </div>

                <div
                    className="mt-16 md:absolute md:bottom-10 md:right-10 md:mt-0 flex items-center gap-3 text-cream/70 animate-fadeUp"
                    style={{ animationDelay: "0.7s" }}
                >
                    <span className="font-script text-gold text-2xl">
                        scroll
                    </span>
                    <span className="h-px w-14 bg-cream/40" />
                </div>
            </div>
        </section>
    );
};

// -----------------------------------------------------------------------------
// Amenity strip (marquee)
// -----------------------------------------------------------------------------
const AmenityStrip = () => {
    const items = [
        { icon: Wifi, label: "Internet in every unit" },
        { icon: Star, label: "Sky full of stars" },
        { icon: Flame, label: "Fire pit nights" },
        { icon: TreePine, label: "Tucked into the trees" },
        { icon: BedDouble, label: "Slow mornings" },
        { icon: UtensilsCrossed, label: "Kitchenettes on-site" },
    ];
    const doubled = [...items, ...items];
    return (
        <section
            aria-label="Amenities"
            className="border-y border-[rgba(196,165,117,0.25)] bg-ivory overflow-hidden"
        >
            <div className="animate-marquee py-5">
                {doubled.map((it, i) => (
                    <span
                        key={i}
                        className="inline-flex items-center gap-3 px-8 text-forest/80"
                    >
                        <it.icon size={16} className="text-gold" />
                        <span className="font-mono-alt text-forest/70">
                            {it.label}
                        </span>
                        <span className="text-gold">✦</span>
                    </span>
                ))}
            </div>
        </section>
    );
};

// -----------------------------------------------------------------------------
// Stays
// -----------------------------------------------------------------------------
const StayCard = ({ stay, reverse }) => {
    return (
        <article
            data-testid={stay.testId}
            className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center py-16 md:py-24 ${
                reverse ? "lg:[&>*:first-child]:order-2" : ""
            }`}
        >
            <div className="lg:col-span-7 relative">
                <div className="relative overflow-hidden rounded-[28px] card-lift zoomable">
                    <img
                        src={stay.image}
                        alt={stay.name}
                        className="w-full h-[380px] md:h-[520px] object-cover"
                        loading="lazy"
                    />
                    <div className="absolute top-5 left-5 bg-cream/90 backdrop-blur px-4 py-2 rounded-full flex items-center gap-2">
                        <span className="text-gold">✦</span>
                        <span className="font-mono-alt text-forest">
                            {stay.subtitle}
                        </span>
                    </div>
                </div>
                <div className="absolute -bottom-6 right-6 md:right-10 bg-forest text-cream px-6 py-4 rounded-2xl flex items-baseline gap-1 shadow-2xl">
                    <span className="font-display text-3xl md:text-4xl">
                        ${stay.price}
                    </span>
                    <span className="text-cream/70 text-sm">
                        {stay.priceLabel}
                    </span>
                </div>
            </div>

            <div className="lg:col-span-5">
                <Eyebrow>The stay</Eyebrow>
                <h3 className="font-display text-forest text-4xl md:text-5xl mt-3 mb-6 leading-tight">
                    {stay.name}
                </h3>

                <ul className="space-y-3 text-forest/85">
                    <li className="flex items-center gap-3">
                        <Users size={18} className="text-gold" />
                        <span>{stay.capacity}</span>
                    </li>
                    <li className="flex items-center gap-3">
                        <BedDouble size={18} className="text-gold" />
                        <span>{stay.bed}</span>
                    </li>
                    <li className="flex items-center gap-3">
                        <Bath size={18} className="text-gold" />
                        <span>{stay.bath}</span>
                    </li>
                    {stay.highlights.map((h) => (
                        <li key={h} className="flex items-center gap-3">
                            {h.toLowerCase().includes("internet") ? (
                                <Wifi size={18} className="text-gold" />
                            ) : h.toLowerCase().includes("kitchen") ? (
                                <UtensilsCrossed
                                    size={18}
                                    className="text-gold"
                                />
                            ) : (
                                <span className="text-gold">✦</span>
                            )}
                            <span>{h}</span>
                        </li>
                    ))}
                </ul>

                <div className="hairline my-8" />

                <a
                    href="#book"
                    data-testid={stay.bookTestId}
                    className="btn-gold inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium"
                >
                    Reserve the {stay.name.toLowerCase()}
                    <ArrowUpRight size={16} />
                </a>
            </div>
        </article>
    );
};

// -----------------------------------------------------------------------------
// Hostaway Calendar Widget — booking section
// -----------------------------------------------------------------------------
const HostawayCalendar = () => {
    const containerRef = useRef(null);
    const mounted = useRef(false);

    useEffect(() => {
        if (mounted.current) return;
        mounted.current = true;

        const init = () => {
            if (
                typeof window !== "undefined" &&
                typeof window.hostawayCalendarWidget === "function" &&
                containerRef.current
            ) {
                // Reset any prior render (hot reload / re-mount safety)
                containerRef.current.innerHTML = "";
                try {
                    window.hostawayCalendarWidget({
                        baseUrl: BOOKING_BASE_URL,
                        listingId: HOSTAWAY_LISTING_ID,
                        numberOfMonths: 2,
                        openInNewTab: true,
                        font: "Manrope",
                        rounded: true,
                        button: {
                            action: "checkout",
                            text: "Book now",
                        },
                        clearButtonText: "Clear dates",
                        color: {
                            mainColor: "#C4A575",
                            frameColor: "#2C3F35",
                            textColor: "#2C3F35",
                        },
                    });
                } catch (err) {
                    // Widget errors shouldn't crash the site.
                    console.error("Hostaway widget init failed:", err);
                }
            }
        };

        const scriptId = "hostaway-calendar-script";
        const existing = document.getElementById(scriptId);
        if (existing) {
            init();
            return;
        }
        const script = document.createElement("script");
        script.id = scriptId;
        script.src = "https://d2q3n06xhbi0am.cloudfront.net/calendar.js";
        script.async = true;
        script.onload = init;
        script.onerror = () =>
            console.error("Failed to load Hostaway calendar script");
        document.body.appendChild(script);
    }, []);

    return (
        <div
            ref={containerRef}
            id="hostaway-calendar-widget"
            data-testid="hostaway-calendar"
            className="hostaway-mount"
        />
    );
};

const BookingSection = () => (
    <section
        id="book"
        data-testid="booking-section"
        className="relative py-24 md:py-32 bg-ivory overflow-hidden"
    >
        <Sparkle className="top-16 left-[8%] animate-twinkle" size={16} />
        <Sparkle
            className="bottom-24 right-[10%] animate-twinkle"
            size={12}
            style={{ animationDelay: "0.9s" }}
        />

        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                <div className="lg:col-span-5">
                    <Eyebrow>Check availability</Eyebrow>
                    <h2 className="font-display text-forest text-5xl md:text-6xl mt-3 leading-tight">
                        Pick your dates,
                        <br />
                        <span className="italic text-gold">book instantly</span>
                        .
                    </h2>
                    <p className="mt-6 text-forest/70 leading-relaxed max-w-md">
                        Live availability for the{" "}
                        <span className="text-forest font-medium">
                            Large Dome
                        </span>
                        . Choose your nights on the calendar and continue to
                        secure checkout.
                    </p>
                    <p
                        data-testid="booking-small-dome-note"
                        className="mt-3 font-mono-alt text-sage"
                    >
                        Small Dome opens for booking soon
                    </p>

                    <div className="hairline my-8" />

                    <div className="space-y-4 text-forest/75">
                        <div className="flex items-start gap-3">
                            <CalendarCheck
                                size={18}
                                className="text-gold mt-1 shrink-0"
                            />
                            <span>
                                Real-time availability — no back-and-forth.
                            </span>
                        </div>
                        <div className="flex items-start gap-3">
                            <Star
                                size={18}
                                className="text-gold mt-1 shrink-0"
                            />
                            <span>
                                Secure checkout opens in a new tab so you keep
                                your place here.
                            </span>
                        </div>
                        <div className="flex items-start gap-3">
                            <Send
                                size={18}
                                className="text-gold mt-1 shrink-0"
                            />
                            <span>
                                Prefer to ask first? Send us a{" "}
                                <a
                                    href="#contact"
                                    className="underline decoration-gold/60 underline-offset-4 hover:text-gold"
                                >
                                    quick note
                                </a>{" "}
                                instead.
                            </span>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <div className="rounded-[28px] bg-cream border border-[rgba(196,165,117,0.35)] p-4 md:p-8 shadow-[0_20px_60px_-30px_rgba(44,63,53,0.25)]">
                        <div className="flex items-center justify-between mb-4 px-1">
                            <span className="inline-flex items-center gap-2 bg-gold/15 text-forest px-3 py-1.5 rounded-full text-xs font-medium">
                                <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                                Large Dome · $299.95 / night
                            </span>
                            <span className="font-mono-alt text-forest/50 hidden sm:inline">
                                Powered by Hostaway
                            </span>
                        </div>
                        <HostawayCalendar />
                        <div className="mt-4 text-center">
                            <a
                                href={HOSTAWAY_BOOKING_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hostaway-fallback-link"
                                className="inline-flex items-center gap-2 font-mono-alt text-forest/60 hover:text-gold transition-colors"
                            >
                                Trouble loading the calendar? Open booking
                                page
                                <ArrowUpRight size={14} />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

// -----------------------------------------------------------------------------
// Bus Waitlist inline form
// -----------------------------------------------------------------------------
const BusWaitlist = () => {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [joined, setJoined] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const trimmed = email.trim();
        if (!trimmed || !/^\S+@\S+\.\S+$/.test(trimmed)) {
            toast.error("Drop a valid email and we'll ping you first.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/bus-waitlist`, { email: trimmed });
            setJoined(true);
            toast.success("You're on the list. May the stay be with you.");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Please try again.";
            toast.error(
                typeof detail === "string" ? detail : "Please try again.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (joined) {
        return (
            <div
                data-testid="bus-waitlist-success"
                className="mt-10 max-w-md p-5 rounded-2xl border border-gold/40 bg-cream/[0.06] backdrop-blur"
            >
                <div className="font-script text-gold text-3xl leading-none">
                    you&apos;re in.
                </div>
                <p className="mt-2 text-cream/75 text-sm leading-relaxed">
                    We&apos;ll ping{" "}
                    <span className="text-gold">{email}</span> the moment the
                    Star Wars bus opens for bookings. First look, first pick.
                </p>
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            data-testid="bus-waitlist-form"
            className="mt-10 max-w-md"
        >
            <div className="flex items-center gap-2 p-1.5 rounded-full border border-cream/25 bg-cream/[0.06] backdrop-blur focus-within:border-gold transition-colors">
                <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="bus-waitlist-email"
                    placeholder="you@somewhere.com"
                    className="flex-1 bg-transparent px-4 py-2.5 text-cream placeholder:text-cream/40 outline-none text-sm"
                    aria-label="Email address for bus waitlist"
                />
                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="bus-waitlist-submit"
                    className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium disabled:opacity-60"
                >
                    {submitting ? "…" : "Notify me"}
                    <ArrowRight size={15} />
                </button>
            </div>
            <p className="mt-3 font-mono-alt text-cream/50">
                First look · No spam · Unsubscribe anytime
            </p>
        </form>
    );
};

const BusReveal = () => {
    return (
        <article
            id="bus"
            data-testid={HOME.stayCardBus}
            className="relative overflow-hidden rounded-[32px] my-16 md:my-24"
        >
            <video
                data-testid={HOME.busVideo}
                className="absolute inset-0 h-full w-full object-cover"
                src={BUS_VIDEO}
                poster={BUS_STILL}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
            />
            <div className="video-scrim bus-scrim" />

            {/* Sparkles */}
            <Sparkle className="top-8 right-[10%] animate-twinkle" size={20} />
            <Sparkle
                className="bottom-16 left-[18%] animate-twinkle"
                size={14}
                style={{ animationDelay: "1.1s" }}
            />

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 p-8 md:p-16 min-h-[560px]">
                <div className="lg:col-span-7 flex flex-col justify-center">
                    <Eyebrow className="text-gold">Coming soon</Eyebrow>
                    <h3 className="font-display text-cream text-5xl md:text-7xl mt-4 leading-[0.95]">
                        The <span className="italic text-gold">Star Wars</span>
                        <br />
                        Bus.
                    </h3>
                    <p className="mt-6 max-w-xl text-cream/80 text-lg leading-relaxed">
                        A converted cruiser reimagined as a hideaway from a
                        galaxy far, far away. King bed, private bath,
                        kitchenette, internet — and details every fan will
                        clock in about two seconds.
                    </p>

                    <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-6 text-cream/85">
                        <div>
                            <div className="text-gold text-2xl font-display">
                                2
                            </div>
                            <div className="font-mono-alt text-cream/60 mt-1">
                                Guests
                            </div>
                        </div>
                        <div>
                            <div className="text-gold text-2xl font-display">
                                King
                            </div>
                            <div className="font-mono-alt text-cream/60 mt-1">
                                Bed
                            </div>
                        </div>
                        <div>
                            <div className="text-gold text-2xl font-display">
                                1
                            </div>
                            <div className="font-mono-alt text-cream/60 mt-1">
                                Bath
                            </div>
                        </div>
                        <div>
                            <div className="text-gold text-2xl font-display">
                                Kitchenette
                            </div>
                            <div className="font-mono-alt text-cream/60 mt-1">
                                On board
                            </div>
                        </div>
                    </div>

                    <BusWaitlist />
                </div>

                <div className="lg:col-span-5 hidden lg:flex items-end justify-end">
                    <div className="text-right">
                        <div className="font-script text-gold text-5xl leading-none">
                            may the stay
                        </div>
                        <div className="font-script text-cream text-6xl leading-none mt-1">
                            be with you
                        </div>
                    </div>
                </div>
            </div>
        </article>
    );
};

const Stays = () => (
    <section
        id="stays"
        data-testid={HOME.staysSection}
        className="relative py-24 md:py-32 bg-cream"
    >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="max-w-2xl mb-8">
                <Eyebrow>The units</Eyebrow>
                <h2 className="font-display text-forest text-5xl md:text-6xl mt-3">
                    Three ways to <span className="italic">stay</span>.
                </h2>
                <p className="mt-5 text-forest/70 leading-relaxed">
                    Every unit comes with fast internet, thoughtful design, and
                    a sky full of stars right outside your door.
                </p>
            </div>

            {STAYS.map((s, i) => (
                <StayCard key={s.id} stay={s} reverse={i % 2 === 1} />
            ))}

            <BusReveal />
        </div>
    </section>
);

// -----------------------------------------------------------------------------
// Gallery
// -----------------------------------------------------------------------------
const Gallery = () => (
    <section
        id="gallery"
        data-testid={HOME.gallerySection}
        className="py-24 md:py-32 bg-ivory"
    >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
                <div className="max-w-2xl">
                    <Eyebrow>Guest diaries</Eyebrow>
                    <h2 className="font-display text-forest text-5xl md:text-6xl mt-3">
                        Through their{" "}
                        <span className="font-script text-gold text-6xl md:text-7xl align-baseline">
                            lenses
                        </span>
                        .
                    </h2>
                </div>
                <p className="text-forest/70 max-w-md leading-relaxed">
                    Real moments from real guests — the fires, the coffees, the
                    mornings that made them stay one more night.
                </p>
            </div>

            <div className="lens-grid">
                {GALLERY.map((src, i) => (
                    <div
                        key={i}
                        data-testid={HOME.galleryItem(i)}
                        className="lens-item zoomable card-lift"
                    >
                        <img
                            src={src}
                            alt={`Guest moment ${i + 1}`}
                            loading="lazy"
                        />
                    </div>
                ))}
            </div>
        </div>
    </section>
);

// -----------------------------------------------------------------------------
// About / included in every stay
// -----------------------------------------------------------------------------
const About = () => (
    <section
        id="about"
        data-testid={HOME.aboutSection}
        className="py-24 md:py-32 bg-cream"
    >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-6">
                <Eyebrow>Included in every stay</Eyebrow>
                <h2 className="font-display text-forest text-5xl md:text-6xl mt-3 leading-tight">
                    Everything you need,
                    <br />
                    <span className="italic text-gold">nothing</span> you don&apos;t.
                </h2>
                <p className="mt-6 text-forest/75 leading-relaxed max-w-lg">
                    We stripped the noise so the trees, the fire and the stars
                    can do what they do best. Every unit — dome or bus — comes
                    ready for a slow, connected stay.
                </p>
            </div>

            <ul className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                {[
                    {
                        icon: Wifi,
                        title: "High-speed internet",
                        copy: "Work from a dome. Stream in the bus. Everywhere.",
                    },
                    {
                        icon: UtensilsCrossed,
                        title: "Kitchenettes",
                        copy: "Coffee, small meals, late-night snacks — sorted.",
                    },
                    {
                        icon: Flame,
                        title: "Fire pit access",
                        copy: "Marshmallows and long conversations included.",
                    },
                    {
                        icon: Star,
                        title: "Stargazing skies",
                        copy: "Low light pollution, high wonder.",
                    },
                ].map((f) => (
                    <li key={f.title} className="flex gap-4">
                        <div className="mt-1 flex-shrink-0 h-10 w-10 rounded-full bg-cream border border-gold/60 flex items-center justify-center">
                            <f.icon size={18} className="text-gold" />
                        </div>
                        <div>
                            <div className="font-display text-forest text-2xl">
                                {f.title}
                            </div>
                            <p className="text-forest/70 mt-1 leading-relaxed">
                                {f.copy}
                            </p>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    </section>
);

// -----------------------------------------------------------------------------
// Contact / Booking Inquiry (Hostaway-ready)
// -----------------------------------------------------------------------------
const Contact = () => {
    const [submitting, setSubmitting] = useState(false);
    const [values, setValues] = useState({
        name: "",
        email: "",
        unit: "",
        check_in: "",
        check_out: "",
        guests: 2,
        message: "",
    });

    const on = (k) => (e) =>
        setValues((v) => ({ ...v, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!values.name || !values.email) {
            toast.error("Please share your name and email so we can reply.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/inquiries`, {
                ...values,
                guests: Number(values.guests) || undefined,
            });
            toast.success(
                "Inquiry received — we'll be in touch within a day.",
            );
            setValues({
                name: "",
                email: "",
                unit: "",
                check_in: "",
                check_out: "",
                guests: 2,
                message: "",
            });
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "Something went wrong. Please try again.";
            toast.error(
                typeof detail === "string" ? detail : "Please try again.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid={HOME.contactSection}
            className="py-24 md:py-32 bg-forest text-cream relative overflow-hidden"
        >
            <Sparkle className="top-16 right-[10%] animate-twinkle" size={16} />
            <Sparkle
                className="bottom-20 left-[8%] animate-twinkle"
                size={12}
                style={{ animationDelay: "0.9s" }}
            />

            <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <Eyebrow className="text-gold">Reserve · Inquire</Eyebrow>
                    <h2 className="font-display text-cream text-5xl md:text-6xl mt-3 leading-tight">
                        Let&apos;s plan your
                        <br />
                        <span className="italic text-gold">paradise</span>.
                    </h2>
                    <p className="mt-6 text-cream/70 leading-relaxed max-w-md">
                        Send us your dates and the unit you have your eye on.
                        We&apos;ll come back with availability and a personal note.
                    </p>

                    <div className="hairline my-10 opacity-40" />

                    <div className="space-y-4 text-cream/85">
                        <div className="flex items-center gap-3">
                            <MapPin size={18} className="text-gold" />
                            <span>Hidden away in paradise</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <Send size={18} className="text-gold" />
                            <a
                                href="mailto:hello@glampinginparadise.com"
                                className="hover:text-gold transition-colors"
                            >
                                hello@glampinginparadise.com
                            </a>
                        </div>
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    data-testid={HOME.contactForm}
                    className="lg:col-span-7 bg-cream/[0.04] backdrop-blur border border-cream/10 rounded-[28px] p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5"
                >
                    <Field label="Your name">
                        <input
                            type="text"
                            required
                            data-testid={HOME.contactName}
                            value={values.name}
                            onChange={on("name")}
                            placeholder="Jane Skywalker"
                            className="input"
                        />
                    </Field>

                    <Field label="Email">
                        <input
                            type="email"
                            required
                            data-testid={HOME.contactEmail}
                            value={values.email}
                            onChange={on("email")}
                            placeholder="you@somewhere.com"
                            className="input"
                        />
                    </Field>

                    <Field label="Which unit?">
                        <select
                            data-testid={HOME.contactUnit}
                            value={values.unit}
                            onChange={on("unit")}
                            className="input"
                        >
                            <option value="">No preference</option>
                            <option value="small-dome">Small Dome</option>
                            <option value="large-dome">Large Dome</option>
                            <option value="bus">
                                Star Wars Bus (waitlist)
                            </option>
                        </select>
                    </Field>

                    <Field label="Guests">
                        <input
                            type="number"
                            min={1}
                            max={8}
                            data-testid={HOME.contactGuests}
                            value={values.guests}
                            onChange={on("guests")}
                            className="input"
                        />
                    </Field>

                    <Field label="Check in">
                        <input
                            type="date"
                            data-testid={HOME.contactCheckIn}
                            value={values.check_in}
                            onChange={on("check_in")}
                            className="input"
                        />
                    </Field>

                    <Field label="Check out">
                        <input
                            type="date"
                            data-testid={HOME.contactCheckOut}
                            value={values.check_out}
                            onChange={on("check_out")}
                            className="input"
                        />
                    </Field>

                    <Field label="Notes" className="md:col-span-2">
                        <textarea
                            rows={4}
                            data-testid={HOME.contactMessage}
                            value={values.message}
                            onChange={on("message")}
                            placeholder="Anniversary? Solo escape? Tell us the vibe."
                            className="input resize-none"
                        />
                    </Field>

                    <div className="md:col-span-2 flex items-center justify-between mt-2 gap-4">
                        <span className="font-mono-alt text-cream/50">
                            We reply within 24 hours
                        </span>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid={HOME.contactSubmit}
                            className="btn-gold inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {submitting ? "Sending…" : "Send inquiry"}
                            <ArrowRight size={16} />
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
};

const Field = ({ label, className = "", children }) => (
    <label className={`block ${className}`}>
        <span className="font-mono-alt text-cream/60 mb-2 block">{label}</span>
        {children}
    </label>
);

// -----------------------------------------------------------------------------
// Footer
// -----------------------------------------------------------------------------
// Custom TikTok icon (lucide doesn't export one)
const TikTokIcon = ({ size = 18, className = "" }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        className={className}
        aria-hidden="true"
    >
        <path d="M19.6 6.8a5.4 5.4 0 0 1-3.3-1.1 5.4 5.4 0 0 1-2-3.7h-3v13.4a2.7 2.7 0 1 1-1.9-2.6v-3a5.7 5.7 0 1 0 4.9 5.6V9.4a8.5 8.5 0 0 0 5.3 1.8V8.1a5.4 5.4 0 0 1 0-1.3z" />
    </svg>
);

const Footer = () => (
    <footer
        data-testid={HOME.footer}
        className="bg-charcoal text-cream/80 pt-20 pb-10 relative overflow-hidden"
    >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-14 border-b border-cream/10">
                <div className="md:col-span-5">
                    <div className="flex items-center gap-3">
                        <img
                            src={LOGO}
                            alt="Glamping in Paradise"
                            className="h-16 w-16 object-contain"
                        />
                        <div className="leading-none">
                            <div className="font-display text-cream text-2xl">
                                Glamping
                            </div>
                            <div className="font-mono-alt text-sage mt-1">
                                In Paradise
                            </div>
                        </div>
                    </div>
                    <p className="mt-6 max-w-sm leading-relaxed text-cream/60">
                        Domed retreats, a Star Wars themed bus, and a night sky
                        that keeps its promises.
                    </p>
                </div>

                <div className="md:col-span-3">
                    <div className="font-mono-alt text-gold mb-4">Explore</div>
                    <ul className="space-y-2.5">
                        <li>
                            <a href="#stays" className="hover:text-gold">
                                Stays
                            </a>
                        </li>
                        <li>
                            <a href="#book" className="hover:text-gold">
                                Book
                            </a>
                        </li>
                        <li>
                            <a href="#gallery" className="hover:text-gold">
                                Gallery
                            </a>
                        </li>
                        <li>
                            <a href="#about" className="hover:text-gold">
                                About
                            </a>
                        </li>
                        <li>
                            <a href="#contact" className="hover:text-gold">
                                Contact
                            </a>
                        </li>
                    </ul>
                </div>

                <div className="md:col-span-4">
                    <div className="font-mono-alt text-gold mb-4">Follow</div>
                    <div className="flex items-center gap-3">
                        <a
                            href="#"
                            data-testid={HOME.footerSocialInstagram}
                            aria-label="Instagram"
                            className="h-11 w-11 rounded-full border border-cream/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"
                        >
                            <Instagram size={18} />
                        </a>
                        <a
                            href="#"
                            data-testid={HOME.footerSocialFacebook}
                            aria-label="Facebook"
                            className="h-11 w-11 rounded-full border border-cream/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"
                        >
                            <Facebook size={18} />
                        </a>
                        <a
                            href="#"
                            data-testid={HOME.footerSocialTiktok}
                            aria-label="TikTok"
                            className="h-11 w-11 rounded-full border border-cream/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"
                        >
                            <TikTokIcon size={18} />
                        </a>
                    </div>
                    <p className="mt-5 text-cream/60 text-sm max-w-xs leading-relaxed">
                        Slow-travel diaries, guest features and the moment the
                        bus is finally ready.
                    </p>
                </div>
            </div>

            <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-cream/50 text-sm">
                <span>
                    © {new Date().getFullYear()} Glamping in Paradise. All
                    rights reserved.
                </span>
                <span className="font-script text-gold text-xl">
                    stay wild, sleep well.
                </span>
            </div>
        </div>
    </footer>
);

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
const Home = () => {
    // Ping the API (non-blocking) — keeps existing behaviour + surfaces
    // backend issues early in dev.
    const pinged = useRef(false);
    useEffect(() => {
        if (pinged.current) return;
        pinged.current = true;
        axios.get(`${API}/`).catch(() => {});
    }, []);

    return (
        <div className="App">
            <Toaster
                position="bottom-right"
                theme="light"
                toastOptions={{
                    style: {
                        background: "var(--brand-cream)",
                        color: "var(--brand-forest)",
                        border: "1px solid rgba(196,165,117,0.35)",
                        borderRadius: "14px",
                    },
                }}
            />
            <Header />
            <main>
                <Hero />
                <AmenityStrip />
                <Stays />
                <BookingSection />
                <Gallery />
                <About />
                <Contact />
            </main>
            <Footer />

            {/* Local styles for the form inputs (kept scoped by class name) */}
            <style>{`
                .input {
                    width: 100%;
                    background: rgba(253, 252, 240, 0.05);
                    border: 1px solid rgba(253, 252, 240, 0.15);
                    color: var(--brand-cream);
                    padding: 12px 14px;
                    border-radius: 12px;
                    font-family: "Manrope", sans-serif;
                    font-size: 15px;
                    transition: border 0.3s, background 0.3s;
                }
                .input::placeholder { color: rgba(253,252,240,0.35); }
                .input:focus {
                    outline: none;
                    border-color: var(--brand-gold);
                    background: rgba(253, 252, 240, 0.08);
                }
                .input[type="date"] { color-scheme: dark; }
                select.input option { color: var(--brand-forest); }
            `}</style>
        </div>
    );
};

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<Home />}>
                        <Route index element={<Home />} />
                    </Route>
                </Routes>
            </BrowserRouter>
        </div>
    );
}

export default App;
